import React, { useState } from 'react'
import ScrollableImageGallery from './chatbot/queries/ScrollableImageGallery';

function formatText(text) {
  // Enhanced regex to capture URLs even when wrapped in formatting markers
  return text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`|__.*?__|~~.*?~~|\[.*?\]\(.*?\)|(?:https?:\/\/|www\.)[^\s]+)/).map((part, index) => {
    // Check if bold text contains a URL
    if (part.startsWith('**') && part.endsWith('**')) {
      const innerText = part.slice(2, -2);
      const urlMatch = innerText.match(/^(https?:\/\/|www\.)[^\s]+$/);
      
      if (urlMatch) {
        // It's a bold URL, render as link with "Click here"
        const href = innerText.startsWith('www.') ? `https://${innerText}` : innerText;
        return <a 
          key={index} 
          href={href} 
          className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700 krunk-font-bold"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </a>;
      }
      return <strong key={index}>{innerText}</strong>;
    } else if (part.startsWith('*') && part.endsWith('*')) {
      const innerText = part.slice(1, -1);
      const urlMatch = innerText.match(/^(https?:\/\/|www\.)[^\s]+$/);
      
      if (urlMatch) {
        // It's an italic URL, render as link with "Click here"
        const href = innerText.startsWith('www.') ? `https://${innerText}` : innerText;
        return <a 
          key={index} 
          href={href} 
          className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700 krunk-italic"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </a>;
      }
      return <em key={index}>{innerText}</em>;
    } else if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index} className="krunk-bg-gray-100 krunk-px-1 krunk-rounded">{part.slice(1, -1)}</code>;
    } else if (part.startsWith('__') && part.endsWith('__')) {
      const innerText = part.slice(2, -2);
      const urlMatch = innerText.match(/^(https?:\/\/|www\.)[^\s]+$/);
      
      if (urlMatch) {
        // It's an underlined URL, render as link with "Click here"
        const href = innerText.startsWith('www.') ? `https://${innerText}` : innerText;
        return <a 
          key={index} 
          href={href} 
          className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </a>;
      }
      return <u key={index}>{innerText}</u>;
    } else if (part.startsWith('~~') && part.endsWith('~~')) {
      return <del key={index}>{part.slice(2, -2)}</del>;
    } else if (part.match(/\[.*?\]\(.*?\)/)) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      return <a 
        key={index} 
        href={match[2]} 
        className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        {match[1]}
      </a>;
    } else if (part.match(/^(?:https?:\/\/|www\.)[^\s]+$/)) {
      // Handle plain URLs
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return <a 
        key={index} 
        href={href} 
        className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        Click here
      </a>;
    }
    return part;
  });
}

function FormattedText({ text = "" }) {
  // Handle null, undefined, or non-string values
  if (!text || typeof text !== 'string') {
    return <div></div>;
  }

  return (
    <div>
      {text.split('\n').map((line, index) => (
        <p key={index} className="krunk-w-full krunk-p-2 krunk-m-0 krunk-break-words">
          {formatText(line)}
        </p>
      ))}
    </div>
  );
}


function MediaContainer({ mediaType, text }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  switch (mediaType) {
    case 'image':
      return (
        <>
          <div className="media-container image-container">
            <img 
              src={text} 
              alt="Chat image" 
              className="krunk-max-w-full krunk-h-auto krunk-rounded-lg krunk-cursor-pointer krunk-hover:opacity-90 krunk-transition-opacity"
              onClick={openModal}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
              title="Click to view full size"
            />
            <div className="error-fallback" style={{ display: 'none' }}>
              <p className="krunk-text-red-500">Failed to load image: {text}</p>
            </div>
          </div>
          
        </>
      );
    
    case 'video':
      return (
        <div className="media-container video-container">
          <video 
            src={text} 
            controls 
            className="krunk-max-w-full krunk-h-auto krunk-rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            Your browser does not support the video tag.
          </video>
          <div className="error-fallback" style={{ display: 'none' }}>
            <p className="krunk-text-red-500">Failed to load video: {text}</p>
          </div>
        </div>
      );
    
    case 'document':
      return (
        <div className="media-container document-container">
          <div className="document-preview krunk-bg-gray-50 krunk-p-4 krunk-rounded-lg krunk-border">
            <div className="document-icon krunk-mb-2">
              📄
            </div>
            <div className="document-info">
              <p className="krunk-font-medium krunk-text-gray-800">
                {text.split('/').pop() || 'Document'}
              </p>
              <a 
                href={text} 
                target="_blank" 
                rel="noopener noreferrer"
                className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700 krunk-text-sm"
              >
                Open Document
              </a>
            </div>
          </div>
        </div>
      );
    
    case 'audio':
      return (
        <div className="media-container audio-container">
          <audio 
            src={text} 
            controls 
            className="krunk-w-full"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            Your browser does not support the audio element.
          </audio>
          <div className="error-fallback" style={{ display: 'none' }}>
            <p className="krunk-text-red-500">Failed to load audio: {text}</p>
          </div>
        </div>
      );
    
    default:
      return <FormattedText text={text} />;
  }
}

function chat(props) {
  const { mediatype = 'text', message, time } = props;
   if (!message?.text || message.text.trim() === '') {
    return null;
  }
  
return (
<div
  className="krunk-flex krunk-flex-col krunk-items-start krunk-mt-2 krunk-pt-0 krunk-pb-0 krunk-text-[13px] krunk-font-normal krunk-font-['Poppins']"
  style={{ color: 'var(--tailwind-buttontext)' }}
>
  <div
    className="krunk-w-4/5 krunk-p-[7px] krunk-rounded-2xl krunk-bg-bubblecolor krunk-text-textcolor"
    style={{
      borderRadius: '0 8px 8px 8px',
    }}
  >
    {mediatype === 'text' ? (
      <FormattedText text={message.text} />
    ) : (
      <MediaContainer mediaType={mediatype} text={message.text} />
    )}
  </div>
</div>

);
}

export default chat